'use client';

import { c as _c } from "react/compiler-runtime";
import type { Role } from '@svs/nova-core/nextjs/auth';
import { useDraw } from '@svs/nova-poolbetting/core/frameworks/nextjs/draw';
import { useCouponId, useCouponPrice, useCouponStatus, useCouponWallets, useNumberOfPopulatedCoupons, useShareableCouponUrl } from '@svs/nova-poolbetting/core/nextjs/coupon';
import { useBalance } from '@svs/nova-poolbetting/core/nextjs/lib/client';
import type { CouponIndex } from '@svs/nova-poolbetting/schema/coupon';
import type { DrawNumber } from '@svs/nova-poolbetting/schema/draw';
import type { Definition } from '@svs/nova-poolbetting/schema/frameworks';
import type { RetailerBalanceResult } from '@svs/nova-poolbetting/schema/marketplace';
import { type ReactNode } from 'react';
import SrowCouponOverview from './SrowCouponOverview';
type SrowCouponOverviewContainerProps = {
  drawNumber: DrawNumber;
  product: Definition['drawProducts'][number];
  retailerData?: RetailerBalanceResult;
  couponIndex?: CouponIndex;
  role: Role;
};

/**
 * Container component that renders the complete sRow coupon overview including participant info and bet buttons.
 *
 * @param props - Component properties containing draw details and configuration
 * @param props.drawNumber - The draw number to display coupon overview for
 * @param props.product - The product definition containing draw product configuration
 * @param props.retailerData - Optional retailer balance data for balance display
 * @param props.couponIndex - Optional specific coupon index to display (defaults to current active coupon)
 * @param props.role - Role of the user (e.g., 'Retailer', 'Player')
 * @returns A React node displaying the complete sRow coupon overview interface
 */
export default function SrowCouponOverviewContainer(t0) {
  const $ = _c(37);
  const {
    drawNumber,
    product,
    retailerData,
    couponIndex,
    role
  } = t0;
  const [couponId, currentCouponIndex] = useCouponId();
  const t1 = couponIndex ?? currentCouponIndex;
  let t2;
  if ($[0] !== couponId || $[1] !== t1) {
    t2 = {
      couponId,
      couponIndex: t1
    };
    $[0] = couponId;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const couponStatus = useCouponStatus(t2);
  const isDisabled = couponStatus === "INVALID_NOT_STARTED";
  const t3 = couponIndex ?? currentCouponIndex;
  let t4;
  if ($[3] !== couponId || $[4] !== t3) {
    t4 = {
      couponId,
      couponIndex: t3
    };
    $[3] = couponId;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const price = useCouponPrice(t4);
  let t5;
  if ($[6] !== couponId) {
    t5 = {
      couponId
    };
    $[6] = couponId;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const activeWallets = useCouponWallets(t5);
  let t6;
  if ($[8] !== couponId) {
    t6 = {
      couponId
    };
    $[8] = couponId;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const nrOfRows = useNumberOfPopulatedCoupons(t6);
  let t7;
  if ($[10] !== drawNumber || $[11] !== product) {
    t7 = {
      drawNumber,
      product
    };
    $[10] = drawNumber;
    $[11] = product;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const drawData = useDraw(t7);
  const t8 = couponIndex ?? currentCouponIndex;
  let t9;
  if ($[13] !== activeWallets || $[14] !== drawNumber || $[15] !== product || $[16] !== retailerData || $[17] !== role || $[18] !== t8) {
    t9 = {
      activeWallets,
      couponIndex: t8,
      drawNumber,
      retailerData,
      product,
      role
    };
    $[13] = activeWallets;
    $[14] = drawNumber;
    $[15] = product;
    $[16] = retailerData;
    $[17] = role;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const {
    balance,
    shouldRedirectUser
  } = useBalance(t9);
  const t10 = couponIndex ?? currentCouponIndex;
  let t11;
  if ($[20] !== couponId || $[21] !== drawNumber || $[22] !== product || $[23] !== t10) {
    t11 = {
      product,
      couponId,
      boardIndex: 0,
      couponIndex: t10,
      drawNumber
    };
    $[20] = couponId;
    $[21] = drawNumber;
    $[22] = product;
    $[23] = t10;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const url = useShareableCouponUrl(t11);
  let t12;
  if ($[25] !== balance || $[26] !== drawData.data.bettingEvent || $[27] !== drawData.data.draw || $[28] !== drawData.data.participants || $[29] !== drawNumber || $[30] !== isDisabled || $[31] !== nrOfRows || $[32] !== price || $[33] !== product || $[34] !== shouldRedirectUser || $[35] !== url) {
    t12 = <SrowCouponOverview drawNumber={drawNumber} product={product} draw={drawData.data.draw} balance={balance} shouldRedirectUser={shouldRedirectUser} url={url} isDisabled={isDisabled} price={price} nrOfRows={nrOfRows} drawParticipants={drawData.data.participants} bettingEvents={drawData.data.bettingEvent} />;
    $[25] = balance;
    $[26] = drawData.data.bettingEvent;
    $[27] = drawData.data.draw;
    $[28] = drawData.data.participants;
    $[29] = drawNumber;
    $[30] = isDisabled;
    $[31] = nrOfRows;
    $[32] = price;
    $[33] = product;
    $[34] = shouldRedirectUser;
    $[35] = url;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  return t12;
}